import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  FlatList,
  Image,
  SectionList,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useQueries, useQuery } from "@tanstack/react-query";
import Svg, {
  Defs,
  LinearGradient,
  RadialGradient,
  Rect,
  Stop,
} from "react-native-svg";
import { RootStackParamList } from "../navigation/types";
import { useSession } from "../session/SessionProvider";
import {
  getItems,
  getProviderTags,
  imageSource,
  JellyfinItem,
} from "../api/jellyfin";
import {
  discoverProviderBrands,
  getProviderBrand,
} from "../data/streamingServices";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { FocusablePressable } from "../components/FocusablePressable";
import { StreamingServiceLogo } from "../components/StreamingServiceLogo";
import { colors, systemSansFont } from "../theme/tokens";
import { ChevronLeft } from "lucide-react-native";
import { Credentials } from "../storage/credentials";
import { MediaShelf } from "../components/MediaShelf";

type Props = NativeStackScreenProps<RootStackParamList, "Streaming">;
type ProviderBrand = {
  name: string;
  slug: string;
  tint: string;
  tintLight: string;
  textColor: string;
};
type ServiceSection = {
  key: "featured" | "more";
  title: string;
  data: Array<Array<ProviderBrand | null>>;
};

const FEATURED_ORDER = [
  "netflix",
  "prime-video",
  "hulu",
  "disney-plus",
  "hbo-max",
  "crunchyroll",
  "apple-tv-plus",
  "paramount-plus",
];
const FEATURED_LIMIT = 6;

function rowsOf<T>(items: T[], columns: number) {
  const rows: T[][] = [];
  for (let index = 0; index < items.length; index += columns)
    rows.push(items.slice(index, index + columns));
  return rows;
}

function BrandMark({
  brand,
  featured,
  compact = false,
}: {
  brand: ProviderBrand;
  featured: boolean;
  compact?: boolean;
}) {
  const supported = [
    "netflix",
    "prime-video",
    "hulu",
    "disney-plus",
    "hbo-max",
    "crunchyroll",
    "paramount-plus",
  ].includes(brand.slug);
  if (supported)
    return (
      <StreamingServiceLogo
        service={brand.slug}
        width={featured ? 220 : 150}
        height={featured ? (compact ? 56 : 64) : 32}
      />
    );
  return (
    <Text
      numberOfLines={1}
      style={featured ? styles.featuredFallback : styles.secondaryFallback}
    >
      {brand.name}
    </Text>
  );
}

function TileHighlight({
  active,
  featured,
}: {
  active: boolean;
  featured: boolean;
}) {
  const opacity = useRef(new Animated.Value(active ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(opacity, {
      toValue: active ? 1 : 0,
      duration: 200,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [active, opacity]);
  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.tileHighlight,
        featured ? styles.featuredHighlight : styles.secondaryHighlight,
        { opacity },
      ]}
    />
  );
}

function FeaturedTile({
  brand,
  width,
  onPress,
}: {
  brand: ProviderBrand;
  width: number;
  onPress: () => void;
}) {
  const compact = width < 400;
  return (
    <FocusablePressable
      accessibilityRole="button"
      accessibilityLabel={`Open ${brand.name}`}
      onPress={onPress}
      style={(focused, pressed) => [
        styles.featuredTile,
        { width, shadowColor: brand.tint },
        focused && styles.focused,
        pressed && styles.pressed,
      ]}
    >
      {(focused, hovered) => (
        <>
          <Svg
            pointerEvents="none"
            width={width}
            height={178}
            style={StyleSheet.absoluteFillObject}
          >
            <Defs>
              <LinearGradient
                id={`top-${brand.slug}`}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <Stop offset="0" stopColor={brand.tintLight} />
                <Stop offset="1" stopColor={brand.tint} />
              </LinearGradient>
              <RadialGradient
                id={`glow-${brand.slug}`}
                cx="50%"
                cy="50%"
                rx="58%"
                ry="82%"
              >
                <Stop
                  offset="0"
                  stopColor={brand.tintLight}
                  stopOpacity="0.22"
                />
                <Stop offset="0.55" stopColor={brand.tint} stopOpacity="0.11" />
                <Stop offset="1" stopColor={brand.tint} stopOpacity="0" />
              </RadialGradient>
            </Defs>
            <Rect
              x="0"
              y="0"
              width={width}
              height={4}
              fill={`url(#top-${brand.slug})`}
            />
            <Rect
              x={Math.round(width * -0.08)}
              y="0"
              width={Math.round(width * 1.16)}
              height="178"
              rx="89"
              fill={`url(#glow-${brand.slug})`}
            />
          </Svg>
          <TileHighlight active={focused || hovered} featured />
          <View pointerEvents="none" style={styles.featuredLogo}>
            <BrandMark brand={brand} featured compact={compact} />
          </View>
        </>
      )}
    </FocusablePressable>
  );
}

function SecondaryTile({
  brand,
  width,
  onPress,
}: {
  brand: ProviderBrand;
  width: number;
  onPress: () => void;
}) {
  return (
    <FocusablePressable
      accessibilityRole="button"
      accessibilityLabel={`Open ${brand.name}`}
      onPress={onPress}
      style={(focused, pressed) => [
        styles.secondaryTile,
        { width },
        focused && styles.focused,
        pressed && styles.pressed,
      ]}
    >
      {(focused, hovered) => (
        <>
          <TileHighlight active={focused || hovered} featured={false} />
          <Svg pointerEvents="none" width={4} height={40}>
            <Defs>
              <LinearGradient
                id={`secondary-${brand.slug}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <Stop offset="0" stopColor={brand.tintLight} />
                <Stop offset="1" stopColor={brand.tint} />
              </LinearGradient>
            </Defs>
            <Rect
              width="4"
              height="40"
              rx="2"
              fill={`url(#secondary-${brand.slug})`}
            />
          </Svg>
          <View pointerEvents="none" style={styles.secondaryLogo}>
            <BrandMark brand={brand} featured={false} />
          </View>
        </>
      )}
    </FocusablePressable>
  );
}

function LoadingTile({
  featured,
  width,
}: {
  featured: boolean;
  width: number;
}) {
  return (
    <View
      style={[
        featured ? styles.featuredLoadingTile : styles.secondaryLoadingTile,
        { width },
      ]}
    />
  );
}

function ServicePreviewCard({
  item,
  credentials,
  width,
  onPress,
}: {
  item: JellyfinItem;
  credentials: Credentials;
  width: number;
  onPress: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const scale = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    Animated.timing(scale, {
      toValue: hovered ? 1.03 : 1,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [hovered, scale]);
  return (
    <FocusablePressable
      accessibilityRole="button"
      accessibilityLabel={`Open ${item.Name ?? "title"}`}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={(focused, pressed) => [
        styles.previewCard,
        { width },
        focused && styles.previewFocused,
        pressed && styles.pressed,
      ]}
    >
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFillObject, { transform: [{ scale }] }]}
      >
        <Image
          source={imageSource(
            credentials,
            item,
            item.BackdropImageTags?.length ? "Backdrop" : "Primary",
            Math.round(width * 2),
            Math.round(width * 1.12),
          )}
          resizeMode="cover"
          style={StyleSheet.absoluteFillObject}
        />
      </Animated.View>
      <Svg
        pointerEvents="none"
        width={width}
        height={Math.round(width / 1.8)}
        style={StyleSheet.absoluteFillObject}
      >
        <Defs>
          <LinearGradient
            id={`preview-${item.Id ?? "item"}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <Stop offset="0" stopColor="#000" stopOpacity="0" />
            <Stop offset="0.52" stopColor="#000" stopOpacity="0.2" />
            <Stop offset="1" stopColor="#000" stopOpacity="0.9" />
          </LinearGradient>
        </Defs>
        <Rect
          width={width}
          height={Math.round(width / 1.8)}
          fill={`url(#preview-${item.Id ?? "item"})`}
        />
      </Svg>
      <Text numberOfLines={2} style={styles.previewTitle}>
        {item.Name ?? "Untitled"}
      </Text>
    </FocusablePressable>
  );
}

function ServiceBrandMark({
  brand,
  width,
  height,
}: {
  brand: ProviderBrand;
  width: number;
  height: number;
}) {
  const supported = [
    "netflix",
    "prime-video",
    "hulu",
    "disney-plus",
    "hbo-max",
    "crunchyroll",
    "paramount-plus",
  ].includes(brand.slug);
  return supported ? (
    <StreamingServiceLogo service={brand.slug} width={width} height={height} />
  ) : (
    <Text
      numberOfLines={1}
      style={[styles.serviceBrandFallback, { lineHeight: height }]}
    >
      {brand.name}
    </Text>
  );
}

function ServiceHero({
  brand,
  items,
  credentials,
  onBack,
  onItemPress,
}: {
  brand: ProviderBrand;
  items: JellyfinItem[];
  credentials: Credentials;
  onBack: () => void;
  onItemPress: (item: JellyfinItem) => void;
}) {
  const { width } = useReferenceWindowDimensions();
  const compact = width <= 760;
  const basePagePadding = width >= 1280 ? 56 : width > 760 ? 32 : 16;
  const pagePadding = Math.max(
    basePagePadding,
    Math.round((width - 1480) / 2),
  );
  const availableWidth = width - pagePadding * 2;
  const brandWidth = compact
    ? availableWidth
    : Math.round(availableWidth * 0.26);
  const previewsWidth = compact
    ? availableWidth
    : availableWidth - brandWidth - 32;
  const previewWidth = compact
    ? Math.min(250, Math.round(width * 0.68))
    : (previewsWidth - 24) / 4;
  const logoHeight = compact ? 36 : 44;
  const logoAspect =
    (
      {
        netflix: 3.7,
        "prime-video": 3.18,
        hulu: 3.04,
        "disney-plus": 1.84,
        "hbo-max": 5.77,
        crunchyroll: 5.83,
        "paramount-plus": 4.34,
      } as Record<string, number>
    )[brand.slug] ?? 4;
  const logoWidth = Math.min(260, Math.round(logoHeight * logoAspect));
  const backdrop = items.find((item) =>
    Boolean(item.BackdropImageTags?.length || item.ImageTags?.Primary),
  );
  return (
    <View
      style={[
        styles.serviceHero,
        !compact && styles.serviceHeroDesktop,
        compact && styles.serviceHeroCompact,
        { paddingHorizontal: pagePadding, backgroundColor: `${brand.tint}18` },
      ]}
    >
      {backdrop ? (
        <Image
          source={imageSource(
            credentials,
            backdrop,
            backdrop.BackdropImageTags?.length ? "Backdrop" : "Primary",
            1600,
          )}
          resizeMode="cover"
          style={styles.serviceHeroBackdrop}
        />
      ) : null}
      <Svg
        pointerEvents="none"
        width={width}
        height={compact ? 276 : 213}
        style={StyleSheet.absoluteFillObject}
      >
        <Defs>
          <LinearGradient
            id="service-hero-horizontal"
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <Stop offset="0" stopColor="rgb(5,7,10)" stopOpacity="0.9" />
            <Stop offset="0.45" stopColor="rgb(5,7,10)" stopOpacity="0.66" />
            <Stop offset="1" stopColor="rgb(5,7,10)" stopOpacity="0.82" />
          </LinearGradient>
          <LinearGradient id="service-hero-bottom" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor={colors.background} stopOpacity="0" />
            <Stop offset="1" stopColor={colors.background} stopOpacity="1" />
          </LinearGradient>
        </Defs>
        <Rect
          width={width}
          height={compact ? 276 : 213}
          fill="url(#service-hero-horizontal)"
        />
        <Rect
          y={compact ? 212 : 149}
          width={width}
          height="64"
          fill="url(#service-hero-bottom)"
        />
      </Svg>
      <FocusablePressable
        accessibilityLabel="Back to Streaming Services"
        onPress={onBack}
        style={(focused, pressed, hovered) => [
          styles.backButton,
          (focused || hovered) && styles.backButtonActive,
          pressed && styles.pressed,
        ]}
      >
        <ChevronLeft color="rgba(255,255,255,0.72)" size={16} />
        <Text style={styles.backText}>Back to Streaming Services</Text>
      </FocusablePressable>
      <View
        style={[styles.serviceHeroRow, compact && styles.serviceHeroRowCompact]}
      >
        <View
          style={[
            styles.serviceBrand,
            compact && styles.serviceBrandCompact,
            { width: brandWidth },
          ]}
        >
          <ServiceBrandMark
            brand={brand}
            width={logoWidth}
            height={logoHeight}
          />
        </View>
        <FlatList
          horizontal
          scrollEnabled={compact}
          showsHorizontalScrollIndicator={false}
          data={items.slice(0, 4)}
          keyExtractor={(item, index) => item.Id ?? `preview-${index}`}
          style={[
            { width: previewsWidth },
            compact && styles.previewListCompact,
          ]}
          contentContainerStyle={styles.previewList}
          renderItem={({ item }) => (
            <ServicePreviewCard
              item={item}
              credentials={credentials}
              width={previewWidth}
              onPress={() => onItemPress(item)}
            />
          )}
        />
      </View>
    </View>
  );
}

function providerItemsQuery(
  tag: string,
  sortBy: string,
  limit: number,
  includeItemTypes = "Movie,Series",
  genre?: string,
  filters?: string,
) {
  return {
    Recursive: true,
    IncludeItemTypes: includeItemTypes,
    Fields: "Overview,Genres,Tags,PrimaryImageAspectRatio",
    EnableImages: true,
    EnableImageTypes: "Primary,Backdrop,Thumb",
    ImageTypeLimit: 1,
    EnableUserData: true,
    Tags: tag,
    SortBy: sortBy,
    SortOrder: "Descending",
    Limit: limit,
    ...(genre ? { Genres: genre } : {}),
    ...(filters ? { Filters: filters } : {}),
  };
}

async function getStreamingProviderItems(
  credentials: Credentials,
  activeTags: string[],
  sortBy: string,
  limit: number,
  includeItemTypes = "Movie,Series",
  genre?: string,
  filters?: string,
) {
  const responses = await Promise.all(
    activeTags.map((tag) =>
      getItems(
        credentials,
        providerItemsQuery(
          tag,
          sortBy,
          limit,
          includeItemTypes,
          genre,
          filters,
        ),
      ),
    ),
  );
  const seen = new Set<string>();
  return responses
    .flat()
    .filter((item) => {
      if (!item.Id) return true;
      if (seen.has(item.Id)) return false;
      seen.add(item.Id);
      return true;
    })
    .slice(0, limit);
}

function StreamingServiceDetail({
  serviceId,
  allTags,
  credentials,
  navigation,
}: {
  serviceId: string;
  allTags: string[];
  credentials: Credentials;
  navigation: Props["navigation"];
}) {
  const { width } = useReferenceWindowDimensions();
  const bottomNavInset = useBottomNavInset();
  const [visibleShelfBudget, setVisibleShelfBudget] = useState(1);
  const pageReveal = useRef(new Animated.Value(0)).current;
  const basePagePadding = width >= 1280 ? 56 : width > 760 ? 32 : 16;
  const pagePadding = Math.max(
    basePagePadding,
    Math.round((width - 1480) / 2),
  );
  const activeTags = useMemo(
    () =>
      allTags.filter(
        (tag) =>
          tag.toLowerCase().startsWith("provider:") &&
          getProviderBrand(tag).slug === serviceId,
      ),
    [allTags, serviceId],
  );
  const brand = useMemo(
    () => getProviderBrand(activeTags[0] ?? `provider:${serviceId}`),
    [activeTags, serviceId],
  );
  const enabled = activeTags.length > 0;
  const primaryTags = useMemo(
    () => {
      const canonical = [...activeTags]
        .sort(
          (left, right) =>
            left.length - right.length || left.localeCompare(right),
        )
        .slice(0, 1);
      return canonical.length ? canonical : [`provider:${brand.name}`];
    },
    [activeTags, brand.name],
  );
  const heroQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "primary", primaryTags],
    queryFn: () =>
      getStreamingProviderItems(credentials, primaryTags, "PlayCount", 20),
    enabled: primaryTags.length > 0,
    staleTime: 60_000,
  });
  const popularQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "primary", primaryTags],
    queryFn: () =>
      getStreamingProviderItems(credentials, primaryTags, "PlayCount", 20),
    enabled: primaryTags.length > 0,
    staleTime: 60_000,
  });
  useEffect(() => {
    pageReveal.stopAnimation();
    pageReveal.setValue(0);
    Animated.timing(pageReveal, {
      toValue: 1,
      duration: 260,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [pageReveal, serviceId]);
  const topQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "top", activeTags],
    queryFn: () =>
      getStreamingProviderItems(credentials, activeTags, "CommunityRating", 20),
    enabled: enabled && visibleShelfBudget >= 2,
    staleTime: 60_000,
  });
  const recentQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "recent", activeTags],
    queryFn: () =>
      getStreamingProviderItems(credentials, activeTags, "PremiereDate", 20),
    enabled: enabled && visibleShelfBudget >= 3,
    staleTime: 60_000,
  });
  const moviesQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "movies", activeTags],
    queryFn: () =>
      getStreamingProviderItems(
        credentials,
        activeTags,
        "CommunityRating",
        20,
        "Movie",
      ),
    enabled: enabled && visibleShelfBudget >= 10,
    staleTime: 60_000,
  });
  const seriesQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "series", activeTags],
    queryFn: () =>
      getStreamingProviderItems(
        credentials,
        activeTags,
        "CommunityRating",
        20,
        "Series",
      ),
    enabled: enabled && visibleShelfBudget >= 11,
    staleTime: 60_000,
  });
  const secondaryShelvesEnabled =
    enabled && heroQuery.isSuccess && popularQuery.isSuccess;
  const recommendedQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "recommended", activeTags],
    queryFn: () =>
      getStreamingProviderItems(
        credentials,
        activeTags,
        "SimilarityScore",
        20,
      ),
    enabled: secondaryShelvesEnabled && visibleShelfBudget >= 4,
    staleTime: 60_000,
  });
  const watchAgainQuery = useQuery({
    queryKey: ["streaming-service", serviceId, "watch-again", activeTags],
    queryFn: () =>
      getStreamingProviderItems(
        credentials,
        activeTags,
        "DatePlayed",
        20,
        "Movie,Series",
        undefined,
        "IsPlayed",
      ),
    enabled:
      secondaryShelvesEnabled &&
      visibleShelfBudget >= 9,
    staleTime: 60_000,
  });
  const tasteGenres = useMemo(() => {
    if (serviceId === "crunchyroll")
      return ["Isekai", "Slice of Life", "Romance", "Fantasy"];
    const counts = new Map<string, number>();
    [...(popularQuery.data ?? []), ...(topQuery.data ?? [])].forEach((entry) =>
      (entry.Genres ?? []).forEach((genre) =>
        counts.set(genre, (counts.get(genre) ?? 0) + 1),
      ),
    );
    return Array.from(counts.entries())
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
      .slice(0, 3)
      .map(([genre]) => genre);
  }, [popularQuery.data, serviceId, topQuery.data]);
  const tasteQueries = useQueries({
    queries: tasteGenres.map((genre, index) => ({
      queryKey: ["streaming-service", serviceId, "taste", genre, activeTags],
      queryFn: () =>
        getStreamingProviderItems(
          credentials,
          activeTags,
          "SimilarityScore",
          20,
          "Movie,Series",
          genre,
        ),
      enabled: secondaryShelvesEnabled && visibleShelfBudget >= 5 + index,
      staleTime: 60_000,
    })),
  });
  const shelves = useMemo(
    () =>
      [
        {
          key: "popular",
          title: `Popular on ${brand.name}`,
          items: popularQuery.data ?? [],
        },
        {
          key: "top",
          title: `Top Rated on ${brand.name}`,
          items: topQuery.data ?? [],
        },
        {
          key: "recent",
          title: `Recently Released on ${brand.name}`,
          items: recentQuery.data ?? [],
        },
        {
          key: "recommended",
          title: "Recommended for You",
          items: recommendedQuery.data ?? [],
        },
        ...tasteGenres.map((genre, index) => ({
          key: `taste-${genre.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
          title:
            serviceId === "crunchyroll"
              ? genre
              : `Matches Your Taste in ${genre}`,
          items: tasteQueries[index]?.data ?? [],
        })),
        {
          key: "watch-again",
          title: "Watch It Again",
          items: watchAgainQuery.data ?? [],
        },
        {
          key: "movies",
          title: "Movies You Might Like",
          items: moviesQuery.data ?? [],
        },
        {
          key: "series",
          title: "Series You Might Like",
          items: seriesQuery.data ?? [],
        },
      ].filter((shelf) => shelf.items.length),
    [
      brand.name,
      moviesQuery.data,
      popularQuery.data,
      recentQuery.data,
      recommendedQuery.data,
      seriesQuery.data,
      serviceId,
      tasteGenres,
      tasteQueries,
      topQuery.data,
      watchAgainQuery.data,
    ],
  );
  const totalShelfBudget = serviceId === "crunchyroll" ? 11 : 10;
  const visibleShelfLoading = [
    popularQuery,
    topQuery,
    recentQuery,
    recommendedQuery,
    ...tasteQueries,
    watchAgainQuery,
    moviesQuery,
    seriesQuery,
  ].some((query) => query.isFetching);
  useEffect(() => setVisibleShelfBudget(5), [serviceId]);
  const openItem = (item: JellyfinItem) =>
    item.Id ? navigation.navigate("Item", { itemId: item.Id }) : undefined;
  const playItem = (item: JellyfinItem) =>
    item.Id ? navigation.navigate("Player", { itemId: item.Id }) : undefined;
  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <MatureTopNav navigation={navigation} active="Streaming" solid />
      <View style={styles.serviceBody}>
      <Animated.View style={[styles.serviceList, { opacity: pageReveal }]}> 
      <FlatList
        data={shelves}
        keyExtractor={(shelf) => shelf.key}
        initialNumToRender={1}
        maxToRenderPerBatch={2}
        windowSize={5}
        removeClippedSubviews
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.35}
        onEndReached={() => {
          setVisibleShelfBudget((current) =>
            Math.min(totalShelfBudget, current + 2),
          );
        }}
        contentContainerStyle={[styles.serviceContent, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
        ListHeaderComponent={
          <>
            <ServiceHero
              brand={brand}
              items={heroQuery.data ?? []}
              credentials={credentials}
              onBack={() => navigation.goBack()}
              onItemPress={openItem}
            />
            <View
              style={
                width > 760
                  ? styles.serviceShelfStartGap
                  : styles.serviceShelfStartGapCompact
              }
            />
          </>
        }
        renderItem={({ item: shelf }) => (
          <View
            style={[
              styles.serviceShelf,
              width <= 760 && styles.serviceShelfCompact,
            ]}
          >
            <MediaShelf
              title={shelf.title}
              items={shelf.items}
              credentials={credentials}
              contentTopPadding={width > 760 ? 17 : 13}
              posterWidth={width > 760 ? 192 : undefined}
              listHorizontalPadding={width > 760 ? pagePadding : undefined}
              cardGap={width > 760 ? 6 : undefined}
              bounded={width > 760}
              onItemPress={openItem}
              onItemPlay={playItem}
            />
          </View>
        )}
        ListEmptyComponent={null}
        ListFooterComponent={
          visibleShelfLoading ? (
            <View style={styles.serviceShelfLoader}>
              <ActivityIndicator color={brand.tintLight} size="small" />
            </View>
          ) : null
        }
      />
      </Animated.View>
      </View>
    </SafeAreaView>
  );
}

export function StreamingServicesScreen({ navigation, route }: Props) {
  const { width } = useReferenceWindowDimensions();
  const { credentials } = useSession();
  const bottomNavInset = useBottomNavInset();
  const [scrolled, setScrolled] = useState(false);
  const landingReveal = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (route.params?.serviceId) return;
    landingReveal.stopAnimation();
    landingReveal.setValue(0);
    Animated.timing(landingReveal, {
      toValue: 1,
      duration: 260,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [landingReveal, route.params?.serviceId]);
  const basePagePadding = width >= 1280 ? 56 : width > 760 ? 32 : 16;
  const pagePadding = Math.max(
    basePagePadding,
    Math.round((width - 1480) / 2),
  );
  const featuredColumns = width >= 1280 ? 3 : width >= 640 ? 2 : 1;
  const secondaryColumns = width >= 1536 ? 4 : width >= 1280 ? 3 : width >= 640 ? 2 : 1;
  const availableWidth = width - pagePadding * 2;
  const compact = width <= 760;
  const pageHeaderHeight = compact ? 94 : 114;
  const featuredTileWidth = Math.floor(
    (availableWidth - 16 * (featuredColumns - 1)) / featuredColumns,
  );
  const secondaryTileWidth = Math.floor(
    (availableWidth - 12 * (secondaryColumns - 1)) / secondaryColumns,
  );
  const tagsQuery = useQuery({
    queryKey: ["provider-tags", credentials?.serverUrl, credentials?.userId],
    queryFn: () => getProviderTags(credentials!),
    enabled: Boolean(credentials),
    staleTime: 10 * 60_000,
  });
  const brands = useMemo(
    () =>
      discoverProviderBrands(tagsQuery.data ?? []).sort((left, right) => {
        const leftIndex = FEATURED_ORDER.indexOf(left.slug);
        const rightIndex = FEATURED_ORDER.indexOf(right.slug);
        const leftPriority =
          leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex;
        const rightPriority =
          rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex;
        return (
          leftPriority - rightPriority || left.name.localeCompare(right.name)
        );
      }),
    [tagsQuery.data],
  );
  const featured = brands.slice(0, FEATURED_LIMIT);
  const more = brands.slice(FEATURED_LIMIT);
  const sections = useMemo<ServiceSection[]>(() => {
    const featuredItems: Array<ProviderBrand | null> = tagsQuery.isLoading
      ? Array.from({ length: FEATURED_LIMIT }, () => null)
      : featured;
    const secondaryItems: Array<ProviderBrand | null> = tagsQuery.isLoading
      ? Array.from({ length: 8 }, () => null)
      : more;
    return [
      {
        key: "featured",
        title: "Featured",
        data: rowsOf(featuredItems, featuredColumns),
      },
      {
        key: "more",
        title: "More services",
        data: rowsOf(secondaryItems, secondaryColumns),
      },
    ];
  }, [featured, featuredColumns, more, secondaryColumns, tagsQuery.isLoading]);
  const openService = (brand: ProviderBrand) =>
    navigation.navigate("Streaming", { serviceId: brand.slug });

  if (!credentials) return null;

  if (route.params?.serviceId) {
    return (
      <StreamingServiceDetail
        serviceId={route.params.serviceId}
        allTags={tagsQuery.data ?? []}
        credentials={credentials}
        navigation={navigation}
      />
    );
  }

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <MatureTopNav
        navigation={navigation}
        active="Streaming"
        overlay
        solid={scrolled}
      />
      <Animated.View style={[styles.landingList, { opacity: landingReveal }]}> 
      <SectionList
        sections={sections}
        keyExtractor={(row, index) => `${row[0]?.slug ?? "empty"}-${index}`}
        stickySectionHeadersEnabled={false}
        showsVerticalScrollIndicator={false}
        onScroll={(event) =>
          setScrolled(event.nativeEvent.contentOffset.y > 80)
        }
        scrollEventThrottle={32}
        contentContainerStyle={[
          styles.content,
          compact && styles.contentCompact,
          { paddingHorizontal: pagePadding },
          bottomNavInset ? { paddingBottom: bottomNavInset } : undefined,
        ]}
        ListHeaderComponent={
          <View
            style={[styles.pageHeader, compact && styles.pageHeaderCompact]}
          >
            <Svg
              pointerEvents="none"
              width={availableWidth}
              height={pageHeaderHeight}
              style={StyleSheet.absoluteFillObject}
            >
              <Defs>
                <LinearGradient
                  id="streaming-header"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <Stop offset="0" stopColor="rgba(17,20,28,0.96)" />
                  <Stop offset="1" stopColor="rgba(8,10,15,0.98)" />
                </LinearGradient>
              </Defs>
              <Rect
                width={availableWidth}
                height={pageHeaderHeight}
                rx={compact ? 26 : 28}
                fill="url(#streaming-header)"
              />
            </Svg>
            <Text
              style={[styles.pageTitle, width <= 760 && styles.pageTitleMobile]}
            >
              Streaming
            </Text>
          </View>
        }
        renderSectionHeader={({ section }) =>
          section.data.length ? (
            <Text
              style={[
                styles.sectionTitle,
                section.key === "featured"
                  ? styles.featuredHeading
                  : styles.moreHeading,
                compact &&
                  section.key === "featured" &&
                  styles.featuredHeadingCompact,
              ]}
            >
              {section.title}
            </Text>
          ) : null
        }
        renderItem={({ item: row, section }) => (
          <FlatList
            horizontal
            scrollEnabled={false}
            data={row}
            keyExtractor={(brand, index) => brand?.slug ?? `loading-${index}`}
            contentContainerStyle={
              section.key === "featured"
                ? styles.featuredGridRow
                : styles.secondaryGridRow
            }
            renderItem={({ item: brand }) =>
              !brand ? (
                <LoadingTile
                  featured={section.key === "featured"}
                  width={
                    section.key === "featured"
                      ? featuredTileWidth
                      : secondaryTileWidth
                  }
                />
              ) : section.key === "featured" ? (
                <FeaturedTile
                  brand={brand}
                  width={featuredTileWidth}
                  onPress={() => openService(brand)}
                />
              ) : (
                <SecondaryTile
                  brand={brand}
                  width={secondaryTileWidth}
                  onPress={() => openService(brand)}
                />
              )
            }
          />
        )}
        ListEmptyComponent={
          !tagsQuery.isLoading ? (
            <View style={styles.empty}>
              <Text style={styles.emptyText}>No streaming services found.</Text>
            </View>
          ) : null
        }
      />
      </Animated.View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  landingList: { flex: 1 },
  content: { paddingTop: 104, paddingBottom: 96 },
  contentCompact: { paddingTop: 136 },
  pageHeader: {
    minHeight: 114,
    paddingHorizontal: 32,
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    borderRadius: 28,
    backgroundColor: "rgba(12,15,22,0.98)",
  },
  pageHeaderCompact: { minHeight: 94, paddingHorizontal: 24, borderRadius: 26 },
  pageTitle: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 48,
    lineHeight: 58,
    fontWeight: "900",
    letterSpacing: -2.8,
  },
  pageTitleMobile: { fontSize: 30, lineHeight: 36, letterSpacing: -1.8 },
  sectionTitle: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontWeight: "900",
    letterSpacing: -1.2,
  },
  featuredHeading: {
    marginTop: 33,
    marginBottom: 18,
    fontSize: 24,
    lineHeight: 29,
  },
  featuredHeadingCompact: {
    marginTop: 25,
    marginBottom: 19,
    fontSize: 20,
    lineHeight: 24,
  },
  moreHeading: {
    marginTop: 27,
    marginBottom: 18,
    fontSize: 20,
    lineHeight: 24,
  },
  featuredGridRow: { gap: 16, marginBottom: 16 },
  secondaryGridRow: { gap: 12, marginBottom: 12 },
  featuredTile: {
    height: 178,
    padding: 20,
    overflow: "hidden",
    justifyContent: "center",
    borderRadius: 26,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: "rgba(14,17,24,0.94)",
    shadowOpacity: 0.19,
    shadowRadius: 36,
  },
  featuredLoadingTile: {
    height: 178,
    borderRadius: 26,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  secondaryLoadingTile: {
    height: 88,
    borderRadius: 22,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  tileHighlight: { ...StyleSheet.absoluteFillObject, borderWidth: 1 },
  featuredHighlight: {
    borderRadius: 26,
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "rgba(255,255,255,0.025)",
  },
  secondaryHighlight: {
    borderRadius: 22,
    borderColor: "rgba(255,255,255,0.07)",
    backgroundColor: "rgba(255,255,255,0.025)",
  },
  featuredLogo: {
    height: 64,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  featuredFallback: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 30,
    lineHeight: 36,
    fontWeight: "900",
    letterSpacing: -1.5,
  },
  secondaryTile: {
    height: 88,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    overflow: "hidden",
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "rgba(255,255,255,0.035)",
  },
  secondaryLogo: { flex: 1, minWidth: 0, justifyContent: "center" },
  secondaryFallback: {
    color: "rgba(255,255,255,0.88)",
    fontFamily: systemSansFont,
    fontSize: 20,
    lineHeight: 25,
    fontWeight: "900",
    letterSpacing: -1,
  },
  focused: { borderWidth: 3, borderColor: colors.text },
  pressed: { opacity: 0.82 },
  empty: {
    marginTop: 32,
    padding: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: "rgba(255,255,255,0.03)",
  },
  emptyText: {
    color: "rgba(255,255,255,0.56)",
    fontFamily: systemSansFont,
    fontSize: 14,
  },
  serviceContent: { paddingBottom: 80 },
  serviceBody: { flex: 1, position: "relative" },
  serviceList: { flex: 1 },
  serviceHero: {
    height: 213,
    paddingTop: 24,
    paddingBottom: 24,
    overflow: "hidden",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.10)",
  },
  serviceHeroDesktop: { marginTop: 12 },
  serviceHeroCompact: {
    height: 276,
    marginTop: -7,
    paddingTop: 21,
    paddingBottom: 20,
  },
  serviceHeroBackdrop: { ...StyleSheet.absoluteFillObject, opacity: 0.25 },
  backButton: {
    alignSelf: "flex-start",
    height: 20,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  backButtonActive: { opacity: 1 },
  backText: {
    color: "rgba(255,255,255,0.72)",
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 20,
  },
  serviceHeroRow: {
    marginTop: 16,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 32,
  },
  serviceHeroRowCompact: {
    marginTop: 19,
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 16,
  },
  serviceBrand: {
    height: 129,
    paddingBottom: 4,
    justifyContent: "flex-end",
    alignItems: "flex-start",
  },
  serviceBrandCompact: {
    height: 36,
    paddingBottom: 0,
    transform: [{ translateY: -4 }],
  },
  serviceBrandFallback: {
    maxWidth: 300,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 36,
    fontWeight: "900",
    letterSpacing: -1.8,
  },
  previewList: { gap: 8 },
  previewListCompact: { marginLeft: -16 },
  previewCard: {
    aspectRatio: 1.8,
    flexShrink: 0,
    overflow: "hidden",
    justifyContent: "flex-end",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: "rgba(255,255,255,0.05)",
  },
  previewFocused: { borderWidth: 3, borderColor: colors.text },
  previewTitle: {
    paddingHorizontal: 10,
    paddingBottom: 10,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 17,
    fontWeight: "600",
    textShadowColor: "rgba(0,0,0,0.9)",
    textShadowRadius: 5,
  },
  serviceLoading: {
    minHeight: 320,
    alignItems: "center",
    justifyContent: "center",
  },
  serviceShelf: { marginBottom: 24 },
  serviceShelfCompact: { marginBottom: 13 },
  serviceShelfStartGap: { height: 23 },
  serviceShelfStartGapCompact: { height: 14 },
  serviceShelfLoader: {
    height: 72,
    alignItems: "center",
    justifyContent: "center",
  },
});
